/**
 * Rendicontazione costi personale: report consuntivo (non prospettico, a
 * differenza di Fabbisogno/Copertura/Fattibilità) filtrabile per periodo,
 * RSA, mansione, persona — a partire dagli export CSV del gestionale
 * presenze. Sola lettura sul filtro/report; il caricamento dati vive in una
 * pagina separata (CostoPersonaleImportPage, /costo-personale/import).
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { Card, Spinner, ErrorBox, StatCard } from "../components/ui/basics";
import AggregatoTable from "./costoPersonale/AggregatoTable";
import ExportPanel from "./costoPersonale/ExportPanel";
import ConfrontoArmonizzazionePanel from "./costoPersonale/ConfrontoArmonizzazionePanel";
import { euro, ore } from "./costoPersonale/format";

const GROUP_BY_OPTIONS = [
  { value: "rsa", label: "RSA" },
  { value: "mansioneCodice", label: "Mansione" },
  { value: "gruppoCosto", label: "Gruppo mansioni" },
  { value: "centroCosto", label: "Centro di costo" },
  { value: "armonizzazione", label: "Armonizzazione" },
  { value: "codiceFiscale", label: "Persona" },
  { value: "periodo", label: "Periodo (mese)" },
];

// Assunto prima/dopo la data di avvio della sua RSA in Peralba (Impostazioni
// → Configurazione RSA) — vedi backend/domain/armonizzazione.js. Non un
// catalogo dinamico: solo 2 stati possibili per costruzione.
const ARMONIZZAZIONE_OPTIONS = [
  { value: "armonizzato", label: "Armonizzato" },
  { value: "ordinario", label: "Ordinario" },
];

export default function CostoPersonalePage() {
  const [periodoDa, setPeriodoDa] = useState("");
  const [periodoA, setPeriodoA] = useState("");
  const [rsa, setRsa] = useState("");
  const [mansione, setMansione] = useState("");
  const [centroCosto, setCentroCosto] = useState("");
  const [armonizzazione, setArmonizzazione] = useState("");
  const [gruppo, setGruppo] = useState("");
  const [codiceFiscale, setCodiceFiscale] = useState("");
  const [groupBy, setGroupBy] = useState("rsa");

  const { data: filtri } = useApi("/costo-personale/filtri");
  const { data: gruppiDisponibili } = useApi("/costo-personale/gruppi");

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (periodoDa) p.set("periodoDa", periodoDa);
    if (periodoA) p.set("periodoA", periodoA);
    if (rsa) p.set("rsa", rsa);
    if (mansione) p.set("mansione", mansione);
    if (centroCosto) p.set("centroCosto", centroCosto);
    if (armonizzazione) p.set("armonizzazione", armonizzazione);
    if (gruppo) p.set("gruppo", gruppo);
    if (codiceFiscale) p.set("codiceFiscale", codiceFiscale);
    p.set("groupBy", groupBy);
    return p.toString();
  }, [
    periodoDa,
    periodoA,
    rsa,
    mansione,
    centroCosto,
    armonizzazione,
    gruppo,
    codiceFiscale,
    groupBy,
  ]);

  const {
    data: report,
    loading,
    error,
  } = useApi(`/costo-personale/report?${query}`, [query]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Rendicontazione costi personale
          </h1>
          <p className="text-sm text-gray-500">
            Costo consuntivo da export gestionale presenze — filtra per periodo,
            RSA, mansione, persona.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <ExportPanel
            filtriCorrente={{
              periodoDa,
              periodoA,
              rsa,
              mansione,
              centroCosto,
              armonizzazione,
              gruppo,
              codiceFiscale,
              groupBy,
            }}
          />
          <Link
            to="/costo-personale/confronto"
            className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            ⚖️ Confronta RSA
          </Link>
          <Link
            to="/costo-personale/import"
            className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            📤 Carica CSV
          </Link>
        </div>
      </div>

      <Card title="Filtri">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Periodo da
            </label>
            <input
              type="month"
              value={periodoDa}
              min={filtri?.periodoMin}
              max={filtri?.periodoMax}
              onChange={(e) => setPeriodoDa(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Periodo a
            </label>
            <input
              type="month"
              value={periodoA}
              min={filtri?.periodoMin}
              max={filtri?.periodoMax}
              onChange={(e) => setPeriodoA(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">RSA</label>
            <select
              value={rsa}
              onChange={(e) => setRsa(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full bg-white"
            >
              <option value="">Tutte</option>
              {(filtri?.rsa || []).map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Mansione</label>
            <select
              value={mansione}
              onChange={(e) => setMansione(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full bg-white"
            >
              <option value="">Tutte</option>
              {(filtri?.mansioni || []).map((m) => (
                <option key={m.codice} value={m.codice}>
                  {m.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Centro di costo
            </label>
            <select
              value={centroCosto}
              onChange={(e) => setCentroCosto(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full bg-white"
            >
              <option value="">Tutti</option>
              {(filtri?.centriCosto || []).map((c) => (
                <option key={c.codice} value={c.codice}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Armonizzazione
            </label>
            <select
              value={armonizzazione}
              onChange={(e) => setArmonizzazione(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full bg-white"
            >
              <option value="">Tutti</option>
              {ARMONIZZAZIONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Gruppo mansioni
            </label>
            <select
              value={gruppo}
              onChange={(e) => setGruppo(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full bg-white"
            >
              <option value="">Tutti</option>
              {(gruppiDisponibili?.items || [])
                .filter((g) => g.attivo)
                .map((g) => (
                  <option key={g.codice} value={g.codice}>
                    {g.nome}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Persona</label>
            <select
              value={codiceFiscale}
              onChange={(e) => setCodiceFiscale(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full bg-white"
            >
              <option value="">Tutte</option>
              {(filtri?.persone || []).map((p) => (
                <option key={p.codiceFiscale} value={p.codiceFiscale}>
                  {p.cognome} {p.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Raggruppa per
            </label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full bg-white"
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {loading && <Spinner label="Calcolo report…" />}
      {error && <ErrorBox message={error} />}

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Costo totale"
              value={euro(report.totale.costoTotale)}
            />
            <StatCard label="Ore totali" value={ore(report.totale.oreTotali)} />
            <StatCard
              label="Costo medio/ora"
              value={euro(report.totale.costoMedioOrario)}
            />
            <StatCard
              label="Dipendenti coinvolti"
              value={report.totale.dipendenti}
            />
            {report.totale.costoPerOspite != null && (
              <>
                <StatCard
                  label={`Costo/ospite (${report.totale.capacita} posti configurati)`}
                  value={euro(report.totale.costoPerOspite)}
                />
                <StatCard
                  label="Costo/ospite al giorno"
                  value={euro(report.totale.costoGiornalieroPerOspite)}
                />
              </>
            )}
          </div>
          {rsa && report.totale.costoPerOspite == null && (
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
              {rsa === "Head Quarter"
                ? "Costo per ospite non applicabile: non è una RSA residenziale (sede amministrativa)."
                : "⚠ Costo per ospite non disponibile: capacità (posti) non configurata per questa RSA in Normative → Configurazione RSA."}
            </p>
          )}

          {groupBy === "gruppoCosto" && report.aggregati.length === 0 && (
            <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
              ⚠ Nessun gruppo mansioni configurato (o nessuno attivo) — creane
              uno da Impostazioni → Gruppi mansioni (costo personale).
            </p>
          )}

          <ConfrontoArmonizzazionePanel
            filtriCorrente={{
              periodoDa,
              periodoA,
              rsa,
              mansione,
              centroCosto,
              gruppo,
              codiceFiscale,
            }}
            groupBy={groupBy}
            mansioniCatalogo={filtri?.mansioni}
          />

          <Card
            title={`Dettaglio per ${GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label}`}
            subtitle={`${report.aggregati.length} gruppi, ${report.righe.length} righe di costo — click su una riga per il dettaglio`}
          >
            <AggregatoTable
              aggregati={report.aggregati}
              righe={report.righe}
              groupBy={groupBy}
              groupByLabel={
                GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label
              }
              mansioni={filtri?.mansioni}
            />
          </Card>
        </>
      )}
    </div>
  );
}
